import Phaser from 'phaser';
import { SCENES, DESIGN_WIDTH, DESIGN_HEIGHT, COLORS, COLORS_HEX } from '../constants';
import { SceneRouter } from '../systems/SceneRouter';
import { Monster } from '../entities/Monster';
import { Boss } from '../entities/Boss';
import { Tower, type TowerType } from '../entities/Tower';
import type { BiomeType } from '../systems/LevelLoader';

// Layout: label on top, sprite below (no overlap). Assets ~50% larger, more vertical spacing.
const CELL_WIDTH = 185;
const CELL_HEIGHT = 96;
const COLS = 9;
const PAD = 6;
const SECTION_HEADER_HEIGHT = 24;
const ROW_PAD = 20;
const LABEL_HEIGHT = 16;   // reserved for label so text doesn't overlap graphics

/** Hidden scene: accessible only via ?preview=assets. Shows all game assets and animations in a grid. */
export class AssetPreviewScene extends Phaser.Scene {
  private contentContainer!: Phaser.GameObjects.Container;
  private contentHeight: number = 0;

  constructor() {
    super({ key: SCENES.ASSET_PREVIEW });
  }

  create(): void {
    SceneRouter.setCurrentScene(SCENES.ASSET_PREVIEW);

    const title = this.add.text(DESIGN_WIDTH / 2, 16, 'Asset Preview (?preview=assets)', {
      fontSize: '18px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    title.setOrigin(0.5);

    const hint = this.add.text(DESIGN_WIDTH / 2, 34, 'Same assets as game — edits show here after reload', {
      fontSize: '11px',
      color: '#aaaaaa',
    });
    hint.setOrigin(0.5);

    this.contentContainer = this.add.container(0, 0);
    let y = 48;

    y = this.addSection(y, 'Trees (wind sway)');
    y = this.addTreePreviews(y);
    y += ROW_PAD;

    y = this.addSection(y, 'Players (run L / run R / dance)');
    y = this.addPlayerPreviews(y);
    y += ROW_PAD;

    y = this.addSection(y, 'Monsters');
    y = this.addMonsterPreviews(y);
    y += ROW_PAD;

    y = this.addSection(y, 'Bosses');
    y = this.addBossPreviews(y);
    y += ROW_PAD;

    y = this.addSection(y, 'Towers');
    y = this.addTowerPreviews(y);

    this.contentHeight = y + 40;
    const needScroll = this.contentHeight > DESIGN_HEIGHT;
    this.cameras.main.setBounds(0, 0, DESIGN_WIDTH, needScroll ? this.contentHeight : DESIGN_HEIGHT);
    if (needScroll) {
      this.input.on('wheel', (_p: Phaser.Input.Pointer, _o: unknown, _dx: number, dy: number) => {
        const cam = this.cameras.main;
        const maxScroll = Math.max(0, this.contentHeight - DESIGN_HEIGHT);
        cam.setScroll(0, Phaser.Math.Clamp(cam.scrollY + dy, 0, maxScroll));
      });
    }
  }

  private addSection(y: number, title: string): number {
    const text = this.add.text(PAD, y, title, {
      fontSize: '13px',
      color: COLORS_HEX.TIKI_GOLD,
      fontStyle: 'bold',
    });
    this.contentContainer.add(text);
    return y + SECTION_HEADER_HEIGHT;
  }

  private addTreePreviews(startY: number): number {
    const biomes: { biome: BiomeType; label: string; keyPrefix: string; maxVariant: number }[] = [
      { biome: 'forest', label: 'Forest', keyPrefix: 'tree-', maxVariant: 9 },
      { biome: 'bog', label: 'Bog', keyPrefix: 'tree-bog-', maxVariant: 8 },
      { biome: 'ruins', label: 'Ruins', keyPrefix: 'tree-ruins-', maxVariant: 10 },
      { biome: 'volcano', label: 'Volcano', keyPrefix: 'tree-volcano-', maxVariant: 12 },
    ];
    let y = startY;
    let col = 0;
    for (const { label, keyPrefix, maxVariant } of biomes) {
      const key = `${keyPrefix}1`;
      if (!this.textures.exists(key)) continue;
      const cellX = PAD + col * (CELL_WIDTH + PAD);
      const cellY = y;
      const lab = this.add.text(cellX + CELL_WIDTH / 2, cellY, label, { fontSize: '11px', color: '#fff' }).setOrigin(0.5, 0);
      this.contentContainer.add(lab);
      const tree = this.add.image(cellX + CELL_WIDTH / 2, cellY + LABEL_HEIGHT + 36, key);
      tree.setScale(0.24);
      tree.setOrigin(0.5, 1);
      this.contentContainer.add(tree);
      if (keyPrefix !== 'tree-volcano-') {
        const swayDeg = 1.8;
        const swayDuration = 3000;
        this.tweens.add({
          targets: tree,
          angle: { from: -swayDeg, to: swayDeg },
          duration: swayDuration,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
      }
      col++;
      if (col >= COLS) {
        col = 0;
        y += CELL_HEIGHT + PAD;
      }
    }
    if (col !== 0) y += CELL_HEIGHT + PAD;
    return y;
  }

  private addPlayerPreviews(startY: number): number {
    const modes: { label: string; run: boolean; flip: boolean; dance: boolean }[] = [
      { label: 'Run →', run: true, flip: false, dance: false },
      { label: 'Run ←', run: true, flip: true, dance: false },
      { label: 'Dance', run: false, flip: false, dance: true },
    ];
    let y = startY;
    for (let p = 1; p <= 4; p++) {
      const baseX = PAD;
      const runKeys = [`player-${p}-run-1`, `player-${p}-run-2`, `player-${p}-run-3`, `player-${p}-run-4`];
      const danceKeys = [`player-${p}-dance-1`, `player-${p}-dance-2`, `player-${p}-dance-3`, `player-${p}-dance-4`];
      const standingKey = `player-${p}-standing`;
      const hasRun = runKeys.every(k => this.textures.exists(k));
      const hasDance = danceKeys.every(k => this.textures.exists(k));
      const hasStand = this.textures.exists(standingKey);

      for (let m = 0; m < modes.length; m++) {
        const mode = modes[m];
        const col = (p - 1) * 3 + m;
        const cellX = baseX + (col % COLS) * (CELL_WIDTH + PAD);
        const cellY = y + Math.floor(col / COLS) * (CELL_HEIGHT + PAD);
        const lab = this.add.text(cellX + CELL_WIDTH / 2, cellY, `P${p} ${mode.label}`, {
          fontSize: '10px',
          color: '#fff',
        }).setOrigin(0.5, 0);
        this.contentContainer.add(lab);

        const frameKeys = mode.dance ? danceKeys : mode.run ? runKeys : [standingKey];
        const firstKey = frameKeys[0];
        if (!this.textures.exists(firstKey)) continue;
        const img = this.add.image(cellX + CELL_WIDTH / 2, cellY + LABEL_HEIGHT + 28, firstKey);
        img.setScale(0.18);
        if (mode.flip) img.setFlipX(true);
        this.contentContainer.add(img);

        if (frameKeys.length > 1) {
          let frameIndex = 0;
          const interval = mode.dance ? 150 : 75;
          this.time.addEvent({
            delay: interval,
            callback: () => {
              if (!img.active) return;
              frameIndex = (frameIndex + 1) % frameKeys.length;
              const k = frameKeys[frameIndex];
              if (this.textures.exists(k)) img.setTexture(k);
              if (mode.dance && frameIndex === 0) {
                img.setFlipX(!img.flipX);
              }
            },
            loop: true,
          });
        }
      }
    }
    return y + Math.ceil(12 / COLS) * (CELL_HEIGHT + PAD);
  }

  private addMonsterPreviews(startY: number): number {
    const types = Monster.getAllTypes();
    let y = startY;
    let col = 0;
    for (const monsterType of types) {
      const key1 = `monster-${monsterType}-1`;
      if (!this.textures.exists(key1)) continue;
      let frameCount = 1;
      while (this.textures.exists(`monster-${monsterType}-${frameCount + 1}`)) frameCount++;

      const cellX = PAD + (col % COLS) * (CELL_WIDTH + PAD);
      const cellY = y + Math.floor(col / COLS) * (CELL_HEIGHT + PAD);
      const lab = this.add.text(cellX + CELL_WIDTH / 2, cellY, monsterType, {
        fontSize: '10px',
        color: '#fff',
      }).setOrigin(0.5, 0);
      this.contentContainer.add(lab);

      const img = this.add.image(cellX + CELL_WIDTH / 2, cellY + LABEL_HEIGHT + 28, key1);
      const stats = Monster.getStats(monsterType);
      img.setScale(0.21 * (stats.imageScaleMultiplier ?? 1));
      this.contentContainer.add(img);

      let frameIndex = 0;
      let flip = false;
      this.time.addEvent({
        delay: 200,
        callback: () => {
          if (!img.active) return;
          frameIndex = (frameIndex + 1) % frameCount;
          const k = `monster-${monsterType}-${frameIndex + 1}`;
          if (this.textures.exists(k)) img.setTexture(k);
          if (frameIndex === 0) flip = !flip;
          img.setFlipX(flip);
        },
        loop: true,
      });

      col++;
    }
    const rows = Math.ceil(col / COLS);
    return y + rows * (CELL_HEIGHT + PAD);
  }

  private addBossPreviews(startY: number): number {
    const types = Boss.getAllTypes();
    let y = startY;
    let col = 0;
    for (const bossType of types) {
      const isGiantBug = bossType === 'giant_bug';
      const firstKey = isGiantBug ? 'boss-giant_bug-walking-1' : `boss-${bossType}-1`;
      if (!this.textures.exists(firstKey)) continue;

      const cellX = PAD + (col % COLS) * (CELL_WIDTH + PAD);
      const cellY = y + Math.floor(col / COLS) * (CELL_HEIGHT + PAD);
      const lab = this.add.text(cellX + CELL_WIDTH / 2, cellY, bossType, {
        fontSize: '10px',
        color: '#fff',
      }).setOrigin(0.5, 0);
      this.contentContainer.add(lab);

      const img = this.add.image(cellX + CELL_WIDTH / 2, cellY + LABEL_HEIGHT + 34, firstKey);
      const stats = Boss.getStats(bossType);
      const scale = (2 * stats.size * 1.8) / Math.max(img.width, img.height);
      img.setScale(scale * 0.48);
      this.contentContainer.add(img);

      if (isGiantBug) {
        const states: Array<'walking' | 'walk_to_fly' | 'flying' | 'fly_to_walk'> = ['walking', 'walk_to_fly', 'flying', 'fly_to_walk'];
        const counts = [7, 3, 4, 5];
        let stateIdx = 0;
        let frameIdx = 0;
        this.time.addEvent({
          delay: 280,
          callback: () => {
            if (!img.active) return;
            const n = counts[stateIdx];
            frameIdx++;
            if (frameIdx >= n) {
              frameIdx = 0;
              stateIdx = (stateIdx + 1) % states.length;
            }
            const k = `boss-giant_bug-${states[stateIdx]}-${frameIdx + 1}`;
            if (this.textures.exists(k)) img.setTexture(k);
          },
          loop: true,
        });
      } else {
        let frameCount = 1;
        while (this.textures.exists(`boss-${bossType}-${frameCount + 1}`)) frameCount++;
        let frameIndex = 0;
        this.time.addEvent({
          delay: 280,
          callback: () => {
            if (!img.active) return;
            frameIndex = (frameIndex + 1) % frameCount;
            const k = `boss-${bossType}-${frameIndex + 1}`;
            if (this.textures.exists(k)) img.setTexture(k);
          },
          loop: true,
        });
      }
      col++;
    }
    const rows = Math.ceil(col / COLS);
    return y + rows * (CELL_HEIGHT + PAD);
  }

  private addTowerPreviews(startY: number): number {
    const types = Tower.getAllTypes();
    const rotating: TowerType[] = ['arrow', 'cannon', 'antiair', 'mortar', 'fire', 'laser'];
    const teslaFrames = 6;
    let y = startY;
    let col = 0;
    for (const towerType of types) {
      const key = `tower-${towerType}`;
      if (!this.textures.exists(key)) continue;

      const cellX = PAD + (col % COLS) * (CELL_WIDTH + PAD);
      const cellY = y + Math.floor(col / COLS) * (CELL_HEIGHT + PAD);
      const lab = this.add.text(cellX + CELL_WIDTH / 2, cellY, towerType, {
        fontSize: '10px',
        color: '#fff',
      }).setOrigin(0.5, 0);
      this.contentContainer.add(lab);

      const img = this.add.image(cellX + CELL_WIDTH / 2, cellY + LABEL_HEIGHT + 30, key);
      img.setScale(0.225);
      this.contentContainer.add(img);

      if (towerType === 'tesla') {
        let frameIndex = 0;
        this.time.addEvent({
          delay: 50,
          callback: () => {
            if (!img.active) return;
            frameIndex = (frameIndex + 1) % teslaFrames;
            const n = frameIndex + 1;
            const pad = n < 10 ? `0${n}` : `${n}`;
            const k = `tower-tesla-${pad}`;
            if (this.textures.exists(k)) img.setTexture(k);
          },
          loop: true,
        });
      } else if (rotating.includes(towerType)) {
        let frameIndex = 0;
        const frameCount = 8;
        this.time.addEvent({
          delay: 120,
          callback: () => {
            if (!img.active) return;
            frameIndex = (frameIndex + 1) % frameCount;
            const n = frameIndex + 1;
            const pad = n < 10 ? `0${n}` : `${n}`;
            const k = `tower-${towerType}-${pad}`;
            if (this.textures.exists(k)) img.setTexture(k);
          },
          loop: true,
        });
      }
      col++;
    }
    const rows = Math.ceil(col / COLS);
    return y + rows * (CELL_HEIGHT + PAD);
  }
}
